import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Container,
  Link,
  List,
  ListItem,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  styled,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/styles';
import React, { useState } from 'react';
import { ReactComponent as FAQBannerIcon } from '../../../src/assets/pulse-faq-help.svg';
import darkTheme from '../../darkTheme';
import { TrackEvent, useTrackPageLoad } from '../analytics';

const StyledLink = styled(Link)(({ theme }) => ({
  textDecoration: 'none',
  color: '#43BCFF',
  opacity: 1,
  '&:hover': {
    color: '#43BCFF',
    opacity: 0.7,
  },
  '&:focus': {
    color: '#7bd0ff',
    opacity: 1,
  },
  '&:selected': {
    color: '#287199',
    opacity: 1,
  },
}));

function FAQBanner() {
  return (
    <Box
      sx={{
        minHeight: 130,
        width: '100%',
        backgroundColor: 'secondary.main',
        borderRadius: 4,
        padding: 2,
        textAlign: 'center',
      }}>
      <Box mt={1} mb={1}>
        <FAQBannerIcon fontSize="large" />
      </Box>
      <Box mt={1.5}>
        <Typography fontSize="large" fontWeight={600} color="primary" sx={{ opacity: 0.8 }}>
          Frequently Asked Questions
        </Typography>
      </Box>
    </Box>
  );
}

function BulletedList({ items, fontSize = 16, paddingLeft = 16 }) {
  // MUI is remarkably bad at handling bulleted lists, so drop into to regular jsx
  return (
    <ul style={{ color: darkTheme.palette.primary.main, margin: 0, paddingLeft: paddingLeft }}>
      {items.map((item, index) => (
        <li key={index} style={{ margin: '4px 0' }}>
          <Typography fontSize={fontSize}>{item}</Typography>
        </li>
      ))}
    </ul>
  );
}

const ComparisonTableCell = styled(TableCell)(({ theme }) => ({
  border: '1px solid rgba(255, 255, 255, 0.31)',
  verticalAlign: 'top',
}));

function FAQComparisonTable() {
  const theme = useTheme();
  const isExtraSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const textFontSize = { xs: 12, sm: 16 };

  const dimensions = [
    {
      label: 'Duration',
      columns: [
        <Typography color="primary" textAlign={'center'} fontSize={textFontSize}>
          ~7 Days
        </Typography>,
        <Typography color="primary" textAlign={'center'} fontSize={textFontSize}>
          in minutes
        </Typography>,
      ],
    },
    {
      label: 'Accuracy',
      columns: [
        <Box textAlign="center">
          <Typography color="primary" fontSize={textFontSize}>
            Very High
          </Typography>
          <Typography color="primary" fontSize={textFontSize}>
            120 Human respondents
          </Typography>
        </Box>,
        <Box textAlign="center">
          <Typography color="primary" fontSize={textFontSize}>
            50%-70% of
          </Typography>
          <Typography color="primary" fontSize={textFontSize}>
            WEVO key findings
          </Typography>
        </Box>,
      ],
    },
    {
      label: 'Deliverables',
      columns: [
        <Box ml={2}>
          <BulletedList
            fontSize={textFontSize}
            paddingLeft={isExtraSmallScreen ? 0 : 16}
            items={[
              'Usability metrics',
              'Sentiment map',
              'Expectations',
              'Analyst-evaluated key findings',
              'Scoring',
            ]}
          />
        </Box>,
        <Box ml={2}>
          <BulletedList
            fontSize={textFontSize}
            paddingLeft={isExtraSmallScreen ? 0 : 16}
            items={['Key findings', 'Sentiment map', 'Chat']}
          />
        </Box>,
      ],
    },
    {
      label: 'Use Cases',
      columns: [
        <Box ml={2}>
          <BulletedList
            fontSize={textFontSize}
            paddingLeft={isExtraSmallScreen ? 0 : 16}
            items={['De-Risk', 'Validate', 'Uncover WHY', 'Compare']}
          />
        </Box>,
        <Box ml={2}>
          <BulletedList
            fontSize={textFontSize}
            paddingLeft={isExtraSmallScreen ? 0 : 16}
            items={['Quick directional feedback', 'When no time or budget for user research']}
          />
        </Box>,
      ],
    },
    {
      label: 'Assets',
      columns: [
        <Box ml={2}>
          <BulletedList
            fontSize={textFontSize}
            paddingLeft={isExtraSmallScreen ? 0 : 16}
            items={['Single page', 'Journey']}
          />
        </Box>,
        <Box ml={2}>
          <BulletedList
            fontSize={textFontSize}
            paddingLeft={isExtraSmallScreen ? 0 : 16}
            items={['Single page', 'Journey']}
          />
        </Box>,
      ],
    },
  ];

  const tableDimensionCellStyles = (index) => {
    if (index === 0) {
      return {
        borderRadius: '16px 0 0 0',
        borderTop: 0,
      };
    }

    if (index === dimensions.length - 1) {
      return {
        borderRadius: '0 0 0 16px',
        borderBottom: 0,
      };
    }

    return {
      borderRadius: '',
    };
  };

  return (
    <Box textAlign={'center'}>
      <Table>
        <TableBody>
          <TableRow>
            <ComparisonTableCell sx={{ border: 0 }}></ComparisonTableCell>
            <ComparisonTableCell
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.20)',
                borderTop: 0,
                borderLeft: 0,
                borderRadius: '16px 0 0 0',
                textAlign: 'center',
                paddingX: { xs: 1, sm: 2 },
              }}>
              <Typography color="primary" fontSize={textFontSize}>
                WEVO Pro
              </Typography>
            </ComparisonTableCell>
            <ComparisonTableCell
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.20)',
                borderTop: 0,
                borderRight: 0,
                borderRadius: '0 16px 0 0',
                textAlign: 'center',
                paddingX: { xs: 1, sm: 2 },
              }}>
              <Typography color="primary" fontSize={textFontSize}>
                WEVO Pulse
              </Typography>
            </ComparisonTableCell>
          </TableRow>
          {dimensions.map((dimension, dimensionIndex) => (
            <TableRow key={dimension.label}>
              <ComparisonTableCell
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.20)',
                  textAlign: 'center',
                  borderLeft: 0,
                  paddingX: { xs: 1, sm: 2 },
                  ...tableDimensionCellStyles(dimensionIndex),
                }}>
                <Typography color="primary" fontSize={textFontSize}>
                  {dimension.label}
                </Typography>
              </ComparisonTableCell>
              {dimension.columns.map((column, index) => (
                <ComparisonTableCell
                  key={`${dimension.label}-value-${index}`}
                  sx={{ paddingX: { xs: 1, sm: 2 } }}>
                  <Box maxWidth={275} fontSize={textFontSize}>
                    {column}
                  </Box>
                </ComparisonTableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  backgroundColor: 'transparent',
  boxShadow: 'none',
  '.MuiAccordionSummary-root': {
    minHeight: 95,
    borderRadius: 4,
    paddingLeft: 32,
    paddingRight: 32,
    '&.Mui-expanded': {
      minHeight: 15,
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '16px 16px 0 0', // corresponds to theme units 4 x 4 = 16px
    },
    '&.Mui-expanded > .MuiAccordionSummary-content': {
      marginTop: 20,
      marginBottom: 10,
    },
  },
  '.MuiAccordionDetails-root': {
    paddingLeft: 32,
    paddingRight: 32,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '0 0 16px 16px', // corresponds to theme units 4 x 4 = 16px
  },
}));

function FAQAccordion({ content, title, isOpen, onChange = () => {} }) {
  return (
    <Box sx={{ border: '1px solid', borderColor: 'secondary.main', borderRadius: 4 }}>
      <StyledAccordion expanded={isOpen} onChange={onChange}>
        <AccordionSummary expandIcon={<ExpandMoreIcon color="primary" fontSize="large" />} px={4}>
          <Typography fontWeight="bold" color="primary">
            {title}
          </Typography>
        </AccordionSummary>
        <AccordionDetails px={4}>{content}</AccordionDetails>
      </StyledAccordion>
    </Box>
  );
}

const FAQ_SECTIONS = [
  {
    id: 'how-to',
    title: 'How can I use WEVO Pulse to get feedback on my asset, and what insights will I receive?',
    content: (
      <Box>
        <Typography color="primary">
          To receive feedback on your asset, simply upload a file in an image format or enter a URL that
          contains the asset you want to analyze. Within minutes, you'll receive key insights produced by WEVO
          Pulse. After a few more minutes, your image will be fully analyzed, and your Pulse will also include
          a sentiment map and feedback on specific sections of the asset.
        </Typography>
        <Box my={2} />
        <Typography fontWeight="600" color="primary">
          What can I test using WEVO Pulse?
        </Typography>
        <List>
          {[
            'a. Web page',
            'b. Figma page or experience',
            'c. App page',
            'd. Messaging',
            'e. Email',
            'f. An article / blog',
            'g. Advertisement',
          ].map((assetType, index) => (
            <ListItem key={index} disableGutters disablePadding>
              <Typography color="primary">{assetType}</Typography>
            </ListItem>
          ))}
        </List>
      </Box>
    ),
  },
  {
    id: 'cost',
    title: 'How much does WEVO Pulse cost?',
    content: (
      <Box>
        <Typography color="primary">
          The beta version of WEVO Pulse is free of charge for seven days. Up to 50 pulses. After that it will
          be a monthly / annual subscription.
        </Typography>
      </Box>
    ),
  },
  {
    id: 'limitations',
    title: 'Are there limitations to what I can test?',
    content: (
      <Box>
        <Typography color="primary" mb={2}>
          WEVO Pulse can evaluate plain text as well as analyze layout, colors, images and the text within
          images. This unique capability ensures a more refined analysis compared to standard image
          recognition, offering deeper insights into your content. With the latest Pulse update, you can also
          evaluate user journeys/experiences, not just single pages.
        </Typography>
        <Typography color="primary">
          WEVO does not recommend using Pulse as a replacement for user research with people. If you are
          interested in a user study, please use WEVO Pro.
        </Typography>
      </Box>
    ),
  },
  {
    id: 'audience-inference',
    title:
      'How does WEVO Pulse determine the personas for my asset, and can I tailor the results to a specific persona?',
    content: (
      <Box>
        <Typography color="primary">
          WEVO Pulse initially presents “proposed" personas based on the asset you provide. At any time, you
          can choose to modify this description to match your specific personas better. Once you've made these
          adjustments, you can rerun the analysis to obtain results tailored to your target personas.
        </Typography>
      </Box>
    ),
  },
  {
    id: 'language-support',
    title: 'Does WEVO Pulse support multiple languages?',
    content: (
      <Box>
        <Typography color="primary">
          Yes, WEVO Pulse currently supports six languages. It can analyze websites, campaigns and messaging
          in: English, Spanish, Portuguese, German, Italian, and French. The analysis appears in English. In
          addition, if you would like to read the analysis in a language other than English just ask in the
          chat functionality to translate the key findings to any of the supported languages above. If you
          would like to use WEVO for another language, please let us know.
        </Typography>
      </Box>
    ),
  },
  {
    id: 'chat-feature',
    title: 'Can I ask WEVO Pulse to provide deeper insights?',
    content: (
      <Box>
        <Typography color="primary">
          Yes, you have the ability to ask WEVO Pulse custom questions. This allows you to gain a more in-depth
          understanding.
        </Typography>
      </Box>
    ),
  },
  {
    id: 'ai',
    title: 'How is AI used in WEVO Pulse?',
    content: (
      <Box>
        <Typography color="primary">
          In WEVO Pulse, there is no human participation, neither in a panel nor analysis. All the results are
          generated solely through generative AI, ensuring an entirely machine-driven process. WEVO leveraged
          over 1 million participants in user studies to help the AI generate enhanced results. WEVO has filed
          over 16 patents, supporting its groundbreaking technology.
        </Typography>
      </Box>
    ),
  },
  {
    id: 'use-chatgpt-instead',
    title: "Can't I just generate similar feedback with ChatGPT?",
    content: (
      <Box>
        <Typography color="primary" my={2}>
          ChatGPT has only knowledge that is based on training data from generic online content. WEVO adds a
          depth of insight in order to provide you with much more valuable insights, based on WEVO’s over 1
          million participants that answered specific UX related questions.
        </Typography>
        <Typography color="primary">Furthermore, WEVO provides you with</Typography>
        <Box my={2} mx={2}>
          <BulletedList
            items={[
              'Sentiment map that identifies specific areas that can be improved.',
              'WEVO enables to get feedback on entire experiences, not just a single page - this is fundamental to user experience analysis.',
              'WEVO enables to easily customize the persona for which you are analyzing.',
              'WEVO enables to store, share and search such findings across the team.',
            ]}
          />
        </Box>
      </Box>
    ),
  },
  {
    id: 'compare-feature',
    title: 'Can I compare different versions of assets using WEVO Pulse?',
    content: (
      <Box>
        <Typography color="primary">
          WEVO is planning to release comparison capability in one of the upcoming releases.
        </Typography>
      </Box>
    ),
  },
  {
    id: 'training-data',
    title: 'Is this product trained by previous WEVO data?',
    content: (
      <Box>
        <Typography color="primary">
          The WEVO Pulse system was improved based on anonymized insights from human-based studies.
        </Typography>
      </Box>
    ),
  },
  {
    id: 'wevo-core-pulse-product-comparison',
    title: 'How does WEVO Pulse compare to WEVO Pro? ',
    content: (
      <Box mt={4} mb={8} ml={{ xs: -3, sm: 0 }}>
        <FAQComparisonTable />
      </Box>
    ),
  },
  {
    id: 'results-interpretation',
    title: 'How should I leverage the information provided by WEVO Pulse?',
    content: (
      <Box>
        <Typography color="primary" mb={2}>
          WEVO Pulse is specifically designed to provide directional insights and is intended to support users
          in working through projects iteratively. It's not meant to replace user research tools such as WEVO
          Pro that include human participants.
        </Typography>
        <Typography color="primary">
          Rather, WEVO Pulse can be used iteratively, helping users refine concepts before investing time and
          resources in full user experience testing.
        </Typography>
      </Box>
    ),
  },
  {
    id: 'ai-caveats',
    title: 'What are AI’s shortcomings that I should be aware of?',
    content: (
      <Box>
        <Typography color="primary">
          WEVO Pulse is designed to provide initial feedback and assist in improving digital experiences, but
          it does come with some limitations:
        </Typography>
        <Box my={2} mx={2}>
          <BulletedList
            items={[
              "It's not designed to provide full assessments of user reactions to digital assets. Rather, only assumed reactions by the model, to the best of its ability.",
              'Generative AI, the underlying technology model that is leveraged here, can provide inaccurate findings. It must be noted that this is a statistical rather than a deterministic model.',
              'It can miss crucial feedback and hence it should not be used as a replacement for a full user study.',
              'It does not provide quantitative feedback. Rather it provide qualitative insights.',
              'Its efficacy with some niche audiences is still being studied and may vary.',
              'It can’t review a website that has moving text as you scroll.',
              'The image limit is 20mb.',
            ]}
          />
        </Box>
        <Typography color="primary">
          WEVO Pulse is best viewed as a stepping stone before implementing a full WEVO analysis rather than a
          standalone solution for comprehensive user or customer experience research.
        </Typography>
      </Box>
    ),
  },
  {
    id: 'data-security',
    title: 'How does WEVO ensure the security of my data?',
    content: (
      <Box>
        <Typography color="primary">
          WEVO takes data security and confidentiality very seriously. Here's how we ensure the protection of
          your data:
        </Typography>
        <Box my={2} mx={2}>
          <BulletedList
            items={[
              "We don't train generative AI on your data.",
              "Data is only accessed by individuals in our company or our vendors who need to see it to ensure the system's quality, minimizing unnecessary exposure.",
              'Upon request, we will delete your data, providing you control over its retention.',
              'Details regarding where the data is stored can be provided per specific security policies, ensuring alignment with legal and regulatory requirements.',
              <span>
                {
                  'WEVO adheres to high security standards and to a detailed security policy which can be found '
                }
                <StyledLink href="https://wevo.ai/privacy/">here</StyledLink>.
              </span>,
            ]}
          />
        </Box>
        <Typography color="primary">
          These measures are designed to uphold the security and confidentiality of your data while it is in
          our care.
        </Typography>
      </Box>
    ),
  },
  {
    id: 'accessible-countries',
    title: 'In which countries is WEVO Pulse currently accessible?',
    content: (
      <Box>
        <Typography color="primary">
          USA, Canada, EU, Australia, Japan, South Korea, UK, Israel, Brazil, and Singapore.
        </Typography>
      </Box>
    ),
  },
  {
    id: 'sentiment-map',
    title: 'How does the Pulse Sentiment Map work?',
    content: (
      <Box>
        <Typography color="primary">
          The Pulse Sentiment Map highlights key portions of the text, categorizing them into positive or
          negative sentiments. Our recent enhancement introduces 'mixed quotes'. This means if a particular
          segment of text elicits both positive and negative responses, the Sentiment Map now cleverly
          summarizes these mixed reactions into a single, comprehensive sentiment analysis. This improvement
          provides a more nuanced understanding of the text, reflecting the complexity and diversity of
          opinions in a consolidated and user-friendly manner.
        </Typography>
      </Box>
    ),
  },
];

export default function FAQ() {
  useTrackPageLoad({
    name: TrackEvent.PULSE_FAQ_PAGE_LOAD,
  });
  const [accordionStates, setAccordionStates] = useState(
    FAQ_SECTIONS.reduce((acc, cur) => {
      acc[cur.id] = false;
      return acc;
    }, {})
  );

  return (
    <Container maxWidth="md">
      <Box my={4} />
      <FAQBanner />
      <Box mt={2} mb={3}>
        <div style={{ textAlign: 'center' }}>
          <Typography color="primary" mb={2}>
            WEVO Pulse, now in Beta, provides feedback on digital assets in minutes, allowing marketing and
            product teams to enhance the quality of their work at every step in the process. It leverages
            generative AI and a data set of tens of thousands of human feedback data points.
          </Typography>
        </div>
      </Box>
      {FAQ_SECTIONS.map((section) => {
        return (
          <Box key={section.id} mb={2}>
            <FAQAccordion
              title={section.title}
              content={section.content}
              isOpen={accordionStates[section.id]}
              onChange={() =>
                setAccordionStates({ ...accordionStates, [section.id]: !accordionStates[section.id] })
              }
            />
          </Box>
        );
      })}
      <div style={{ textAlign: 'center', marginTop: '35px' }}>
        <Typography color="primary" mb={2}>
          Can’t find what you’re looking for? Please reach us at{' '}
          <StyledLink href="mailto:pulsehelp@wevo.ai">pulsehelp@wevo.ai</StyledLink>.
        </Typography>
      </div>
    </Container>
  );
}
